import { http } from './http';
import { ROUTES } from './config';
import type {
  Location,
  LocationInput,
  LocationCountry,
  LocationRegion,
  Suburb,
  SuburbInput,
  ProductLocationPricingRow,
  ProductLocationPricingInput,
  BulkPricingInput,
  BulkPricingResult,
} from '../types/ecommerce';

/** LocationsController client — shippable regions + per-product location price/shipping overrides. */
export const locationsApi = {
  /** Locations ARE cities — ?regionId= narrows to one Region (the hierarchy's "cities" level),
   * omit it for the full flat list. */
  list(includeInactive = false, regionId?: string): Promise<Location[]> {
    const params = new URLSearchParams();
    if (includeInactive) params.set('includeInactive', 'true');
    if (regionId) params.set('regionId', regionId);
    const qs = params.toString();
    return http.get(`${ROUTES.locations.base}${qs ? `?${qs}` : ''}`, { anonymous: true });
  },

  listCountries(): Promise<LocationCountry[]> {
    return http.get(ROUTES.locations.countries, { anonymous: true });
  },

  listRegions(countryId?: string): Promise<LocationRegion[]> {
    const qs = countryId ? `?countryId=${countryId}` : '';
    return http.get(`${ROUTES.locations.regions}${qs}`, { anonymous: true });
  },

  listSuburbs(locationId: string): Promise<Suburb[]> {
    return http.get(ROUTES.locations.suburbs(locationId), { anonymous: true });
  },

  createSuburb(locationId: string, body: SuburbInput): Promise<Suburb> {
    return http.post(ROUTES.locations.suburbs(locationId), body);
  },

  removeSuburb(locationId: string, suburbId: string): Promise<void> {
    return http.delete(`${ROUTES.locations.suburbs(locationId)}/${suburbId}`);
  },

  create(body: LocationInput): Promise<{ id: string }> {
    return http.post(ROUTES.locations.base, body);
  },

  update(id: string, body: LocationInput): Promise<void> {
    return http.put(`${ROUTES.locations.base}/${id}`, body);
  },

  remove(id: string): Promise<void> {
    return http.delete(`${ROUTES.locations.base}/${id}`);
  },

  copyTo(id: string, targetId: string): Promise<void> {
    return http.post(ROUTES.locations.copyTo(id, targetId), {});
  },

  getProductPricing(productId: string): Promise<ProductLocationPricingRow[]> {
    return http.get(ROUTES.locations.productPricing(productId));
  },

  setProductPricing(productId: string, body: ProductLocationPricingInput): Promise<void> {
    return http.put(ROUTES.locations.productPricing(productId), body);
  },

  bulkPricing(body: BulkPricingInput): Promise<BulkPricingResult> {
    return http.put(ROUTES.locations.bulkPricing, body);
  },
};
